import { create } from 'zustand';
import { SectionManifest, ViewDSL } from '../types';
import { ChatMessage } from '../ui/ChatInterface';

export interface AgentState {
  // Project data
  currentProject: {
    sections: SectionManifest[];
    views: ViewDSL[];
    tokens: Record<string, any>;
    projectPath: string;
  };
  
  // Chat state
  messages: ChatMessage[];
  isProcessing: boolean;
  
  // UI state
  selectedStory?: string;
  splitPanePosition: number;
  
  // Agent operations
  lastOperation?: {
    type: string;
    target: string;
    timestamp: Date;
    success: boolean;
  };
}

export interface AgentActions {
  // Project actions
  updateProject: (project: Partial<AgentState['currentProject']>) => void;
  addSection: (section: SectionManifest) => void;
  updateSection: (id: string, updates: Partial<SectionManifest>) => void;
  removeSection: (id: string) => void;
  
  // Chat actions
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  clearMessages: () => void;
  setProcessing: (processing: boolean) => void;
  
  // UI actions
  setSelectedStory: (storyId?: string) => void;
  setSplitPanePosition: (position: number) => void;
  
  // Operation tracking
  recordOperation: (operation: AgentState['lastOperation']) => void;
  
  // Initialization
  initializeAgent: (projectPath: string) => Promise<void>;
}

export type AgentStore = AgentState & AgentActions;

export const useAgentStore = create<AgentStore>((set, get) => ({
  // Initial state
  currentProject: {
    sections: [],
    views: [],
    tokens: {},
    projectPath: process.cwd()
  },
  messages: [],
  isProcessing: false,
  splitPanePosition: 60,
  
  // Project actions
  updateProject: (project) => {
    set((state) => ({
      currentProject: { ...state.currentProject, ...project }
    }));
  },
  
  addSection: (section) => {
    set((state) => ({
      currentProject: {
        ...state.currentProject,
        sections: [...state.currentProject.sections, section]
      }
    }));
  },
  
  updateSection: (id, updates) => {
    set((state) => ({
      currentProject: {
        ...state.currentProject,
        sections: state.currentProject.sections.map(section =>
          section.id === id ? { ...section, ...updates } : section
        )
      }
    }));
  },
  
  removeSection: (id) => {
    set((state) => ({
      currentProject: {
        ...state.currentProject,
        sections: state.currentProject.sections.filter(section => section.id !== id)
      }
    }));
  },
  
  // Chat actions
  addMessage: (message) => {
    const fullMessage: ChatMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random()}`,
      timestamp: new Date()
    };
    
    set((state) => ({
      messages: [...state.messages, fullMessage]
    }));
  },
  
  clearMessages: () => {
    set({ messages: [] });
  },
  
  setProcessing: (processing) => {
    set({ isProcessing: processing });
  },
  
  // UI actions
  setSelectedStory: (storyId) => {
    set({ selectedStory: storyId });
  },
  
  setSplitPanePosition: (position) => {
    set({ splitPanePosition: position });
  },
  
  // Operation tracking
  recordOperation: (operation) => {
    set({ lastOperation: operation });
  },
  
  // Initialization
  initializeAgent: async (projectPath) => {
    try {
      // TODO: Load actual project data from filesystem
      // For now, use mock data
      const mockSections: SectionManifest[] = [];
      const mockViews: ViewDSL[] = [];
      const mockTokens = {};
      
      set((state) => ({
        currentProject: {
          ...state.currentProject,
          projectPath,
          sections: mockSections,
          views: mockViews,
          tokens: mockTokens
        },
        messages: [
          {
            id: `init-${Date.now()}`,
            type: 'system',
            content: `🚀 Dezzy AI Agent initialized!\n\nProject: ${projectPath}\nReady to help you build your design system.`,
            timestamp: new Date()
          }
        ]
      }));
    } catch (error) {
      console.error('Failed to initialize agent:', error);
      
      set((state) => ({
        messages: [
          {
            id: `error-${Date.now()}`,
            type: 'system', 
            content: `❌ Failed to initialize: ${error instanceof Error ? error.message : 'Unknown error'}`,
            timestamp: new Date()
          }
        ]
      }));
    }
  }
}));

// Selector hooks for performance
export const useProjectData = () => useAgentStore((state) => state.currentProject);
export const useChatState = () => useAgentStore((state) => ({
  messages: state.messages,
  isProcessing: state.isProcessing,
  addMessage: state.addMessage,
  setProcessing: state.setProcessing
}));
export const useUIState = () => useAgentStore((state) => ({
  selectedStory: state.selectedStory,
  splitPanePosition: state.splitPanePosition,
  setSelectedStory: state.setSelectedStory,
  setSplitPanePosition: state.setSplitPanePosition
}));